import _ from "lodash";
import React, { useState } from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import I18nHOC from "UIComponents/I18nHOC";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import classes from "./AttachmentItemV3.scss";
import { downloadFromUrl } from "modules/Services";
import {
  getThumbnailIcon,
  htmlToText,
  getLabelValue,
  onEnterOrSpaceKeyDown,
} from "Utils";
import {
  DownloadOutlined,
  JournalAddOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";
import { ModelContent } from "../AttachmentCard/AttachmentCard";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { IconButton } from "@toddle-edu/ds-web";

const AttachmentItemV3 = props => {
  const {
    attachment,
    t,
    isDisabledMode,
    horizontalCardStyle,
    mode,
    showCreatePostButton,
    showDeleteForSelfInViewMode,
    deleteAttachment,
    attachmentGroup,
    userId,
    attachmentBottomComponent,
    iconStyles = {},
  } = props;

  const progressValue = parseInt(_.get(attachment, `progress`, "101"));

  //The progress Value is between 0-100 when its being uploaded, and once uploaded
  //the attachment doesn't have progress in it, so by default its 101, which means its created
  const isAttachmentCreated = progressValue > 100;
  const { id, type, mimeType, createdBy } = attachment;
  const iconSvg = getThumbnailIcon({ type, mimeType });

  const [showMediaModal, setShowMediaModal] = useState(false);

  const toggleMediaModal = value => {
    setShowMediaModal(value);
  };

  const onDownloadClick = e => {
    e.stopPropagation();
    const { downloadFromUrl, attachment } = props;
    downloadFromUrl({
      attachment: attachment,
      shouldAskForSave: true,
    });
  };

  const onDeleteClick = e => {
    e.stopPropagation();

    const attachmentGroupId = _.get(attachmentGroup, "id", "");

    if (deleteAttachment) {
      deleteAttachment(attachmentGroupId);
    }
  };

  const onAttachmentClick = e => {
    e.stopPropagation();
    const { attachment, onClickAttachmentCard, attachmentGroup } = props;

    if (onClickAttachmentCard) {
      onClickAttachmentCard(attachment.id, attachmentGroup, attachment);
    } else {
      if (!showMediaModal) {
        setShowMediaModal(true);
      }
    }
  };

  const onClickCreatePostLocal = e => {
    e.stopPropagation();
    const { attachment, onClickCreatePost } = props;
    if (onClickCreatePost) {
      onClickCreatePost(attachment);
    }
  };

  const stopPropagationHandler = e => {
    e.stopPropagation();
  };

  const showAttachmentItemBottomComponent =
    _.isFunction(attachmentBottomComponent) && attachmentBottomComponent();

  return (
    <div className={classes.attachmentItem}>
      <div
        className={classes.container}
        style={horizontalCardStyle}
        onClick={
          isDisabledMode || !isAttachmentCreated ? null : onAttachmentClick
        }
        onKeyDown={e => {
          onEnterOrSpaceKeyDown(
            e,
            isDisabledMode || !isAttachmentCreated ? null : onAttachmentClick
          );
        }}
        tabIndex={0}
      >
        <div className={classes.innerContainer}>
          <div className={classes.iconContainer}>
            <span className={classes.icon} style={iconStyles}>
              {iconSvg}
            </span>
          </div>
          <div className={classes.rightContainer}>
            <div
              className={classes.titleContainer}
              dangerouslySetInnerHTML={{
                __html: htmlToText(getLabelValue({ item: attachment, t }), {
                  wordwrap: 130,
                  tags: {
                    a: { options: { ignoreHref: true } },
                    ul: { options: { itemPrefix: " " } },
                  },
                }),
              }}
            />

            <div className={classes.iconsContainer}>
              {mode == "view" && showCreatePostButton && (
                <LinkWithTooltip
                  tooltip={t("journal:add_to_portfolio")}
                  placement={"bottom"}
                  href="#"
                  id="tooltip-CreatePostSvg"
                  trigger={["hover", "focus"]}
                  linkProps={{
                    tabIndex: 0,
                  }}
                >
                  <IconButton
                    onClick={onClickCreatePostLocal}
                    onKeyDown={e => {
                      onEnterOrSpaceKeyDown(e, onClickCreatePostLocal);
                    }}
                    dsVersion="2.0"
                    size="small"
                    type="fill"
                    variant="neutral"
                    shouldStopPropagation
                    icon={<JournalAddOutlined />}
                  />
                </LinkWithTooltip>
              )}
              {isAttachmentCreated &&
                type !== "LINK" &&
                type !== "WORKBOOK" &&
                type !== "QUESTION" && (
                  <LinkWithTooltip
                    tooltip={t("common:download")}
                    placement={"bottom"}
                    href="#"
                    id="tooltip-DownloadIcon"
                    trigger={["hover", "focus"]}
                    linkProps={{
                      tabIndex: 0,
                    }}
                  >
                    <IconButton
                      onClick={onDownloadClick}
                      onKeyDown={e => {
                        onEnterOrSpaceKeyDown(e, onDownloadClick);
                      }}
                      dsVersion="2.0"
                      size="small"
                      type="fill"
                      variant="neutral"
                      shouldStopPropagation
                      icon={<DownloadOutlined />}
                    />
                  </LinkWithTooltip>
                )}
              {showDeleteForSelfInViewMode && userId == _.get(createdBy, "id") && (
                <LinkWithTooltip
                  tooltip={t("common:delete")}
                  placement={"bottom"}
                  href="#"
                  id="tooltip-DeleteIcon"
                  trigger={["hover", "focus"]}
                  linkProps={{
                    tabIndex: 0,
                  }}
                >
                  <IconButton
                    onClick={onDeleteClick}
                    onKeyDown={e => {
                      onEnterOrSpaceKeyDown(e, onDeleteClick);
                    }}
                    dsVersion="2.0"
                    size="small"
                    type="fill"
                    variant="neutral"
                    shouldStopPropagation
                    icon={<DeleteOutlined />}
                  />
                </LinkWithTooltip>
              )}
            </div>
          </div>
          <ModelContent
            attachments={[attachment]}
            toggleMediaModal={toggleMediaModal}
            showMediaModal={showMediaModal}
            mode={mode}
          />
        </div>
        {showAttachmentItemBottomComponent && (
          <div className={classes.attachmentDivider}></div>
        )}
        {showAttachmentItemBottomComponent && (
          <div onClick={stopPropagationHandler}>
            {attachmentBottomComponent()}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { attachment } = ownProps;

  const userInfo = getUserInfo();
  const userId = userInfo.id;

  if (_.get(attachment, "isPost", false)) {
    const attachmentObj = _.last(_.get(attachment, "attachments", []));
    const isWorkbook = _.isEqual(_.get(attachmentObj, "type", ""), "WORKBOOK");
    if (isWorkbook) {
      return {
        attachment: { ...attachmentObj, isPost: true },
        attachmentGroup: attachment,
        userId,
      };
    } else {
      return {
        attachment: { ...attachmentObj, id: attachment.id, isPost: true },
        attachmentGroup: attachment,
        userId,
      };
    }
  }

  return {
    attachment,
    userId,
  };
};

const mapActionCreators = {
  downloadFromUrl,
};

AttachmentItemV3.defaultProps = {
  horizontalCardStyle: {},
  showDeleteForSelfInViewMode: false,
};

export default compose(
  I18nHOC({ resource: ["common", "journal"] }),
  connect(mapStateToProps, mapActionCreators)
)(AttachmentItemV3);
